// The BYOD join flow has no login cookie; its credential is the pod's
// processing key from the join response. Stored once here (per tab), it is
// attached to every API call as X-Processing-Key, and appended as ?key= to
// media URLs (<video>/<img> cannot send headers). Logged-in pages never set
// it and keep authenticating with their session cookie.
const CLIENT_KEY_STORAGE = "blinc-processing-key"

export function setClientProcessingKey(key) {
    try {
        if (key) sessionStorage.setItem(CLIENT_KEY_STORAGE, key)
        else sessionStorage.removeItem(CLIENT_KEY_STORAGE)
    } catch {
        /* storage unavailable (private mode) — API calls just go keyless */
    }
}

export function clientProcessingKey() {
    try {
        return sessionStorage.getItem(CLIENT_KEY_STORAGE) || null
    } catch {
        return null
    }
}

// A query string for a GET/DELETE's parameters: fetch sends no body on
// those verbs, so filters passed as "data" (log_type, the device filters)
// used to be dropped on the floor and the server saw none of them.
export function queryString(params) {
    if (!params || typeof params !== "object" || params instanceof FormData) return ""
    const q = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue
        q.append(key, typeof value === "object" ? JSON.stringify(value) : String(value))
    }
    return q.toString()
}

// Append the key to a media URL when present: "url" -> "url?key=..."
export function withClientKey(url) {
    const key = clientProcessingKey()
    if (!key) return url
    return url + (url.includes("?") ? "&" : "?") + "key=" + encodeURIComponent(key)
}

export class ApiService {
    getWSSProtocol() {
        return window.location.protocol === "https:" ? "wss:" : "ws:"
    }

    getEndpoint() {
        return window.location.protocol + "//" + window.location.host + "/"
    }

    getVideoServerEndpoint() {
    return window.location.host;
  }

  getAudioWebsocketEndpoint() {
    return (
      this.getWSSProtocol() +
      "//" +
      window.location.host +
      "/audio_socket"
    );
  }

    getVideoWebsocketEndpoint() {
    return (
         this.getWSSProtocol() +
      "//" + this.getVideoServerEndpoint() +"/video_socket"
    );

  }

  getAudioPosthocWebsocketEndpoint() {
    return this.getWSSProtocol() + "//" + window.location.host + "/audio_posthoc_socket";
  }

  getVideoPosthocWebsocketEndpoint() {
    return this.getWSSProtocol() + "//" + window.location.host + "/video_posthoc_socket";
  }

   _generateHeaders(headers,data) {
    let h = {}
    if(!(data instanceof FormData)){
      h =  {
        "Content-Type": "application/json",
        "Accept": "application/json"
      };
    }
    const clientKey = clientProcessingKey()
    if (clientKey) {
      h["X-Processing-Key"] = clientKey;
    }

        let key = '';
        let val ='';
      for (const property in headers) {
        if (headers.hasOwnProperty(property)) {
          key = (typeof property === 'string')? property : JSON.stringify(property)
          val = (typeof headers[property] === 'string')? headers[property] : JSON.stringify(headers[property])
          h[key] = val;
        }
      }
      return h;
    }

    // Single fetch wrapper — every verb helper below delegates here.
    // FormData bodies are sent raw (and _generateHeaders skips the JSON
    // content type for them); everything else is JSON-encoded.
    _request(method, apipath, data, headers) {
        const options = {
            method,
            mode: "cors",
            credentials: "include",
            // API responses reflect mutable server state; never let the browser
            // serve a stale cached copy. Without this, a GET re-fetched right
            // after a mutation (e.g. the students list after a merge) could
            // return the pre-mutation body, so the page never updated.
            cache: "no-store",
            headers: this._generateHeaders(headers || {}, data),
            redirect: "follow",
        }
        let path = apipath
        if (method === "GET" || method === "DELETE") {
            const query = queryString(data)
            if (query) path += (path.includes("?") ? "&" : "?") + query
        } else if (data !== undefined) {
            options.body = data instanceof FormData ? data : JSON.stringify(data)
        }
        return fetch(this.getEndpoint() + path, options)
    }

    get(apipath, headers) {
        return this._request("GET", apipath, undefined, headers)
    }

    delete(apipath, headers) {
        return this._request("DELETE", apipath, undefined, headers)
    }

    post(apipath, data, headers) {
        return this._request("POST", apipath, data, headers)
    }

    postFiles(apipath, data) {
        return this._request("POST", apipath, data, {})
    }

    put(apipath, data, headers) {
        return this._request("PUT", apipath, data, headers)
    }

    httpRequestCall(apipath, type, data) {
        return this.httpRequestCallWithHeader(apipath, type, data, {})
    }

    httpRequestCallWithHeader(apipath, type, data, headers) {
        if (type === "GET" || type === "DELETE" || type === "POST" || type === "PUT") {
            // GET/DELETE: data becomes the query string (see _request).
            return this._request(type, apipath, data, headers)
        }
        return undefined
    }
}
